import { getAuthorName, type ArticleData } from '@benzinga/article-manager';
import type { MetaProps } from '@benzinga/seo';

import type { SitePageConfig } from '../../_components/site-page/pageConfig';
import { buildClientRouteMeta } from './buildClientRouteMeta';
import type { RouteMetaPayload } from './routeMeta';

const compactStrings = (values: (string | null | undefined)[] = []): string[] =>
  values.filter((value): value is string => Boolean(value));

export function shouldDisablePageTracking(
  routeMeta: RouteMetaPayload | null,
  pageConfig: SitePageConfig = {},
): boolean {
  if (routeMeta?.disablePageTracking) return true;
  if (pageConfig.error === 404 || pageConfig.error === 410) return true;
  if (pageConfig.statusCode === 404 || pageConfig.statusCode === 410) return true;
  return false;
}

export function resolveChartbeatMeta(
  routeMeta: RouteMetaPayload | null,
  pageConfig: SitePageConfig = {},
): { authorName: string; sections: string[] } {
  const metaProps = routeMeta?.metaProps ?? pageConfig.metaProps;
  const article = routeMeta?.article as ArticleData | undefined;
  const articleAuthorName = article ? getAuthorName(article) : '';

  const authorName =
    articleAuthorName ||
    metaProps?.dimensions?.authorName ||
    metaProps?.author ||
    'Benzinga';

  const articleSections = compactStrings(
    [...(article?.channels ?? []), ...(article?.tags ?? [])].map(category => category.name),
  );

  const metaSections = metaProps?.dimensions?.channels?.length
    ? metaProps.dimensions.channels
    : (metaProps?.vertical ?? []);
  const sections = articleSections.length ? articleSections : metaSections;

  return { authorName, sections };
}

function resolveSegmentMeta(
  routeMeta: RouteMetaPayload | null,
  scrollMeta: MetaProps | null,
  pathname: string,
  pageConfig: SitePageConfig,
): MetaProps {
  if (scrollMeta) return scrollMeta;
  if (routeMeta?.metaProps) return routeMeta.metaProps;
  if (pageConfig.metaProps) return pageConfig.metaProps;
  return buildClientRouteMeta(pathname, pageConfig.pageTargeting);
}

export function mergeRoutePayload(
  routeMeta: RouteMetaPayload | null,
  pageConfig: SitePageConfig = {},
): RouteMetaPayload {
  return {
    ...routeMeta,
    pageTargeting: routeMeta?.pageTargeting ?? pageConfig.pageTargeting,
    disableRaptiveFooter: routeMeta?.disableRaptiveFooter ?? pageConfig.disableRaptiveFooter ?? false,
  };
}

export interface ResolvedPageAnalytics {
  shouldTrack: boolean;
  meta: MetaProps;
  chartbeat: { authorName: string; sections: string[] } | null;
  payload: RouteMetaPayload;
}

export function resolvePageAnalytics({
  routeMeta,
  pageConfig = {},
  pathname,
  scrollMeta = null,
}: {
  routeMeta: RouteMetaPayload | null;
  pageConfig?: SitePageConfig;
  pathname: string;
  scrollMeta?: MetaProps | null;
}): ResolvedPageAnalytics {
  const shouldTrack = !shouldDisablePageTracking(routeMeta, pageConfig);
  const payload = mergeRoutePayload(routeMeta, pageConfig);
  const meta = resolveSegmentMeta(routeMeta, scrollMeta, pathname, pageConfig);
  const chartbeat = shouldTrack ? resolveChartbeatMeta(routeMeta, pageConfig) : null;

  return { shouldTrack, meta, chartbeat, payload };
}
